import { ReactNode } from 'react';
import SharedSvg from './shared-svg';
import { Status } from './status-chip';
import StatusLabel, { StatusType } from './status-label';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface SimpleFormHeaderNewProps {
  title: string;
  lastSavedDate?: number;
  /** The words before the date, e.g. "Last published"; defaults to "Last saved". */
  lastSavedLabel?: string;
  isSaving?: boolean;
  status?: Status;
  /** Optional buttons rendered at the right end of the header, after last saved and status. */
  actions?: ReactNode;
  /** Optional content beside the title, such as a status chip. */
  titleAdornment?: ReactNode;
}

export default function SimpleFormHeaderNew({
  title,
  lastSavedDate,
  lastSavedLabel = 'Last saved',
  isSaving,
  status,
  actions,
  titleAdornment,
}: SimpleFormHeaderNewProps) {
  const theme = useTheme();

  const statusValue = (status?.toUpperCase() as StatusType) ?? 'UNKNOWN';
  return (
    <Card
      sx={{
        borderBottom: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'light'
            ? theme.palette.divider
            : theme.palette.semantic.outline.light,
        borderRadius: 2,
        p: 1.5,
        flexShrink: 0,
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1.8}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SharedSvg variant="red" width={16} height={16} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: theme.palette.semantic.text.title,
              pt: 0.2,
            }}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {isSaving ? (
            <>
              <CircularProgress
                size={16}
                sx={{ color: theme.palette.text.primary }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                Saving...
              </Typography>
            </>
          ) : lastSavedDate && lastSavedDate > 0 ? (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                {lastSavedLabel}:{' '}
                {new Date(lastSavedDate).toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Typography>
            </>
          ) : null}
          {statusValue !== 'UNKNOWN' ? (
            <StatusLabel status={statusValue} />
          ) : null}
          {actions}
        </Stack>
      </Stack>
    </Card>
  );
}
